/* eslint-disable react/forbid-prop-types */
import { Banner, Frame, SageClassnames, Tooltip } from '@kajabi/sage-react';
import { useElements } from '@stripe/react-stripe-js';
import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import CustomFields from 'apps/commerce/common/checkout/components/CustomFields/CustomFields';
import PayButton from 'apps/commerce/common/checkout/components/PayButton';
import ServiceAgreement from 'apps/commerce/common/checkout/components/ServiceAgreement';
import useCheckoutSettings from 'apps/commerce/common/checkout/hooks/useCheckoutSettings';
import AddressInput from 'apps/commerce/common/checkout/inputs/AddressInput';
import EmailInput from 'apps/commerce/common/checkout/inputs/EmailInput';
import NameInput from 'apps/commerce/common/checkout/inputs/NameInput';
import OptInCheckbox from 'apps/commerce/common/checkout/inputs/OptInCheckbox';
import PaymentDetails from 'apps/commerce/common/checkout/inputs/PaymentDetails';
import PhoneInput from 'apps/commerce/common/checkout/inputs/PhoneInput';
import SaveCardCheckbox from 'apps/commerce/common/checkout/inputs/SaveCardCheckbox';
import SavedCard from 'apps/commerce/common/checkout/inputs/SavedCard';
import AbandonmentModal from 'apps/commerce/common/checkout/components/AbandonmentModal';
import { localizedDateWithYear, minorUnitAmount } from 'apps/commerce/common/checkout/util';
import { GiftModal, useShouldShowGiftOptions } from 'apps/commerce/common/GiftModal';
import SecureCheckout from 'apps/commerce/common/checkout/components/SecureCheckout';
import { CheckoutConfigData } from 'apps/commerce/embedded_checkout_preview/types';
import useCheckoutLayoutStyles from 'apps/commerce/embedded_checkout_preview/hooks/useCheckoutLayoutStyles';
import useCheckoutButtonStyles from 'apps/commerce/embedded_checkout_preview/hooks/useCheckoutButtonStyles';
import GiftOffers from './GiftOffers';
import { PaymentSummaryText } from './PaymentSummaryText';
import { PriceDisplay } from './PriceDisplay';
import { PriceSummary } from './PriceSummary';
import QuantitySelector from './QuantitySelector';
import PayPalButton from '../inputs/PayPalButton';
import PriceOverrideInput from '../inputs/PriceOverrideInput';
import TaxIdInput from '../inputs/TaxIdInput';
import useContainerQuery from '../hooks/useContainerQuery';

import '../styles/style.scss';

interface Props {
  checkoutConfig: CheckoutConfigData;
  checkoutData: any;
}

function EmbeddedCheckoutForm({ checkoutConfig, checkoutData }: Props) {
  const {
    getValues,
    setValue,
    watch,
    formState: { isValid, errors },
  } = useFormContext();
  const { t } = useTranslation();

  const {
    offer,
    price,
    site,
    nameRequired,
    phoneNumberRequired,
    addressRequired,
    collectTaxId,
    priceOverrideRequired,
    member,
    stripe,
    offerPurchase,
    shouldShowGiftModalContents,
  } = getValues();
  const { shouldShowGiftOptions, setShouldShowGiftOptions } = useShouldShowGiftOptions(false);
  const { serviceAgreementRequired, customFields, isCustomFieldsCompleted } = useCheckoutSettings();

  const paymentProvider = watch('paymentProvider');
  const isPaymentDetailComplete = watch('isPaymentDetailComplete');
  const isPaymentInProgress = watch('isPaymentInProgress');
  const priceOverride = watch('priceOverride');
  const useSavedCard = watch('useSavedCard');
  const serviceAgreementChecked = watch('serviceAgreementChecked');
  const giftDetails = watch('giftDetails');
  const giftIsActive = giftDetails && Object.keys(giftDetails).length > 0;

  const [showAbandonmentModal, setShowAbandonmentModal] = React.useState(false);
  // These conditions need to match the `CheckoutStripeElements` conditions for rendering Stripe's
  // Elements component otherwise this will error.
  if (stripe) {
    // the conditionality of this won't change per render, so we can safely disable the rule
    // eslint-disable-next-line react-hooks/rules-of-hooks
    setValue('elements', useElements());
  }

  const isLoggedIn = !!member;
  const alreadySubscribed = offerPurchase?.type === 'subscription';
  const shouldDisableForm =
    checkoutData.isOfferUnavailable ||
    !!isPaymentInProgress ||
    alreadySubscribed ||
    !!checkoutData.aboveQuantityLimit;

  const validOfferPriceOverride =
    minorUnitAmount(priceOverride, price.exponent) >= Math.max(price.amount, 50);
  const isDoingFreePWYW =
    priceOverrideRequired &&
    checkoutData.isOfferFree &&
    (parseFloat(priceOverride) === 0 || !priceOverride);
  const requiresCardDetails =
    !(checkoutData.isOfferFree || isDoingFreePWYW) &&
    stripe &&
    paymentProvider !== 'external_paypal';
  const isReadyToSubmit =
    !shouldDisableForm &&
    isValid &&
    (customFields ? isCustomFieldsCompleted : true) &&
    (!requiresCardDetails || useSavedCard || isPaymentDetailComplete) &&
    (validOfferPriceOverride || !priceOverrideRequired || isDoingFreePWYW) &&
    (!serviceAgreementRequired || serviceAgreementChecked);
  const shouldDisplayPaymentDetails =
    (stripe && paymentProvider === 'external_paypal') ||
    (requiresCardDetails && !isDoingFreePWYW && !useSavedCard);
  const shouldDisplayPreviewTooltip = isReadyToSubmit && checkoutData.siteUserSignedIn;
  const disableSubmit = checkoutData.siteUserSignedIn || !isReadyToSubmit;
  const isRecurring = price.priceType === 'recurring';
  const providerAllowsSavingCard = paymentProvider === 'card';
  const offerPurchaseDate = offerPurchase?.date;
  const isQuantityEnabled = offer.checkoutQuantityEnabled;
  const isOfferGiftable = offer.giftableOffer;

  const customModalClassNames = isOfferGiftable ? 'checkout-modal--gift-offers' : null;

  const layoutStyles = useCheckoutLayoutStyles(checkoutConfig);
  const buttonStyles = useCheckoutButtonStyles(checkoutConfig);

  const { containerRef, isWideLayout } = useContainerQuery<HTMLFormElement>(800);

  const handleGiftModalCancel = () => {
    if (
      giftIsActive &&
      (giftDetails.recipientEmail ||
        giftDetails.purchaserName ||
        giftDetails.giftDeliveryDate ||
        giftDetails.message !== t('gift_offers.message'))
    ) {
      // only show the abandonment modal if the user has changed at least 1 field
      setShowAbandonmentModal(true);
    } else {
      setValue('giftDetails', {});
      setValue('shouldShowGiftModalContents', false);
      setShouldShowGiftOptions(false);
    }
  };

  const handleGiftModalContinue = () => {
    setValue('shouldShowGiftModalContents', false);
    setShouldShowGiftOptions(false);
  };

  const handleAbandonmentCancel = () => {
    setShowAbandonmentModal(false);
  };

  const handleAbandonmentConfirm = () => {
    setShowAbandonmentModal(false);
    setShouldShowGiftOptions(false);
    setValue('shouldShowGiftModalContents', false);
    setValue('giftDetails', {});
  };

  const handleGiftOffer = () => {
    setValue('shouldShowGiftModalContents', true);
    setShouldShowGiftOptions(true);
  };

  return (
    <div className="embedded-checkout" style={{ ...layoutStyles }}>
      {offerPurchaseDate && (
        <Banner
          active={true}
          type="secondary"
          text={t('form.already_purchased', {
            offer: offer.title,
            date: localizedDateWithYear(offerPurchaseDate),
          })}
          link={{
            href: '/library',
            name: t('form.take_me_there'),
            rel: 'no-opener',
          }}
        />
      )}
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
      <form
        ref={containerRef}
        onKeyPress={(e) => e.key === 'Enter' && e.preventDefault()}
        onSubmit={checkoutData.submitForm}
        className={`${shouldShowGiftOptions ? 'visually-hidden' : 'embedded-checkout__body'} ${
          isWideLayout ? 'embedded-checkout__body--wide' : ''
        }`}
      >
        <div className="embedded-checkout__form">
          {!isWideLayout && (
            <PriceDisplay
              offer={offer}
              displayPrice={checkoutData.displayPrice}
              isWideLayout={false}
            />
          )}
          <>
            {priceOverrideRequired && (
              <PriceOverrideInput
                displayPrice={checkoutData.displayPrice}
                disabled={shouldDisableForm}
              />
            )}
          </>
          <div data-display-layout="sm">{isQuantityEnabled && <QuantitySelector />}</div>
          <fieldset className="embedded-checkout__fieldset">
            <legend className={`embedded-checkout__legend ${SageClassnames.TYPE.HEADING_4}`}>
              {t('form.contact')}
            </legend>
            <EmailInput
              disabled={isLoggedIn || shouldDisableForm}
              defaultValue={isLoggedIn ? member.email : ''}
              label={t('form.email')}
            />
            <OptInCheckbox disabled={shouldDisableForm} />
            {nameRequired && <NameInput disabled={shouldDisableForm} />}
            {addressRequired && <AddressInput disabled={shouldDisableForm} />}
            {phoneNumberRequired && <PhoneInput disabled={shouldDisableForm} />}
            {collectTaxId && <TaxIdInput disabled={shouldDisableForm} />}
          </fieldset>
          {customFields?.length ? (
            <fieldset className="embedded-checkout__fieldset">
              <legend className={`embedded-checkout__legend ${SageClassnames.TYPE.HEADING_4}`}>
                {t('form.additional_information')}
              </legend>
              <CustomFields disabled={shouldDisableForm} customFields={customFields} />
            </fieldset>
          ) : null}
          {!checkoutData.isOfferFree && stripe && (
            <fieldset className="embedded-checkout__fieldset">
              <legend className={`embedded-checkout__legend ${SageClassnames.TYPE.HEADING_4}`}>
                {t('form.payment_method')}
              </legend>
              {member?.savedCardDetails && <SavedCard savedCardDetails={member.savedCardDetails} />}
              {shouldDisplayPaymentDetails && <PaymentDetails disabled={shouldDisableForm} />}
              {requiresCardDetails &&
                checkoutData.showSaveCardCheckbox &&
                !useSavedCard &&
                providerAllowsSavingCard && (
                  <SaveCardCheckbox
                    disabled={shouldDisableForm}
                    saveCardDefaultValue={checkoutData.saveCardDefaultValue}
                  />
                )}
            </fieldset>
          )}
          <div
            className="embedded-checkout__summary embedded-checkout__summary--mobile"
            style={{ display: isWideLayout ? 'none' : 'block' }}
          >
            {isOfferGiftable && (
              <GiftOffers handleGiftOffer={handleGiftOffer} giftIsActive={giftIsActive} />
            )}
            <PriceSummary
              key="price-summary-mobile"
              displayPrice={checkoutData.displayPrice}
              displayOverridePrice={checkoutData.displayOverridePrice}
              dueNowPriceInCents={checkoutData.dueNowPriceInCents}
              taxLoading={checkoutData.taxLoading}
              isCouponLoading={checkoutData.isCouponLoading}
              isLinkInBioPlan={checkoutData.isLinkInBioPlan}
              couponError={checkoutData.couponError}
              isWideLayout={isWideLayout}
            />
          </div>
          <Frame align={Frame.ALIGNMENTS.TOP_CENTER} className="embedded-checkout__cta">
            <ServiceAgreement disabled={shouldDisableForm} />
            {shouldDisplayPreviewTooltip && (
              <div className="site-user-tooltip">
                <Tooltip.Element
                  className="site-user-tooltip"
                  content="Checkout is disabled while you're logged in as a Kajabi site admin"
                />
              </div>
            )}
            {paymentProvider === 'external_paypal' &&
            !isPaymentInProgress &&
            !alreadySubscribed &&
            !checkoutData.isOfferFree ? (
              <PayPalButton
                offerToken={offer.token}
                isRecurring={isRecurring}
                clientId={offer.paypalClientId}
                merchantId={offer.paypalMerchantId}
                onSubmit={checkoutData.submitForm}
                disable={disableSubmit}
              />
            ) : (
              <PayButton
                disableSubmit={disableSubmit}
                isPaymentInProgress={!!isPaymentInProgress}
                dueNowPriceInCents={checkoutData.dueNowPriceInCents}
                buttonStyles={buttonStyles}
                siteUserSignedIn={checkoutData.siteUserSignedIn}
              />
            )}
            <PaymentSummaryText />
            {errors.payment && (
              <div className="sage-form-field--error">
                <div className="sage-input__message">{errors.payment.message}</div>
              </div>
            )}
          </Frame>
        </div>
        <div
          className="embedded-checkout__summary"
          style={{ display: isWideLayout ? 'block' : 'none' }}
        >
          {isWideLayout && (
            <PriceDisplay
              offer={offer}
              displayPrice={checkoutData.displayPrice}
              isWideLayout={true}
            />
          )}
          {isQuantityEnabled && <QuantitySelector />}
          {isOfferGiftable && (
            <GiftOffers handleGiftOffer={handleGiftOffer} giftIsActive={giftIsActive} />
          )}
          <PriceSummary
            key="price-summary"
            displayPrice={checkoutData.displayPrice}
            displayOverridePrice={checkoutData.displayOverridePrice}
            dueNowPriceInCents={checkoutData.dueNowPriceInCents}
            taxLoading={checkoutData.taxLoading}
            isCouponLoading={checkoutData.isCouponLoading}
            isLinkInBioPlan={checkoutData.isLinkInBioPlan}
            couponError={checkoutData.couponError}
            isWideLayout={isWideLayout}
          />
        </div>
        <footer className="embedded-checkout__footer">
          <SecureCheckout />
        </footer>
      </form>
      {shouldShowGiftOptions && (
        <>
          <GiftModal
            className="gift-modal"
            onClose={null}
            onContinue={handleGiftModalContinue}
            onCancel={handleGiftModalCancel}
          />
          {ReactDOM.createPortal(
            <AbandonmentModal
              showModal={showAbandonmentModal}
              handleAbandonmentCancel={handleAbandonmentCancel}
              handleAbandonmentConfirm={handleAbandonmentConfirm}
            />,
            document.body,
          )}
        </>
      )}
    </div>
  );
}
export default EmbeddedCheckoutForm;
